import { useState } from 'react';
import { ComboboxField } from '@/components/forms/ComboboxField';
import { useCountries } from '@/hooks/useCountries';
import { useStates } from '@/hooks/useStates';
import { useCitySearch } from '@/hooks/useCitySearch';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { resolveHqCascade } from '@/lib/utils/hq-location.utils';

/**
 * A headquarters address as the three reference-data ids the API stores, plus
 * the chosen city's NAME.
 *
 * The name is carried alongside the id because the city list is fetched per
 * search term - once the query moves on, the chosen city is no longer in the
 * results, so a label derived from them would blank out the field the moment
 * the admin typed again. Country and state need no equivalent: their lists are
 * held in full, so their labels are always resolvable from the id.
 */
export interface HqLocationValue {
  countryId?: number;
  stateId?: number;
  cityId?: number;
  cityName?: string;
}

/** Per-level validation messages, in the shape React Hook Form produces. */
export interface HqLocationErrors {
  countryId?: string;
  stateId?: string;
  cityId?: string;
}

export interface HqLocationFieldsProps {
  value: HqLocationValue;
  onChange: (next: HqLocationValue) => void;
  countryLabel: string;
  stateLabel: string;
  cityLabel: string;
  labelVariant?: 'bold' | 'caps';
  errors?: HqLocationErrors;
}

/**
 * Country -> state -> city cascade (US) or country -> city (non-US, UX sheet
 * r105), controlled by value/onChange.
 *
 * Country and state filter their already-fetched lists in memory (both are
 * small and bounded); only city goes to the Backend per search, because that
 * table is ~153k rows and cannot be prefetched in full.
 *
 * Narrowing a level clears every level below it INSIDE the change handler,
 * never in an effect: an effect fires on mount too, which is why an
 * effect-driven copy of this cascade needs a `useRef` previous-value guard to
 * stop it wiping a persisted selection on remount.
 *
 * Used by the founder's profile editor and the admin console's correction form.
 * The create wizard still runs its own effect-and-guard cascade and was
 * deliberately left alone (out of scope for MYN-132), so this is not yet the
 * single implementation.
 */
export function HqLocationFields({
  value,
  onChange,
  countryLabel,
  stateLabel,
  cityLabel,
  labelVariant = 'caps',
  errors,
}: HqLocationFieldsProps) {
  const [countryQuery, setCountryQuery] = useState('');
  const [stateQuery, setStateQuery] = useState('');
  const [cityQuery, setCityQuery] = useState('');

  const { data: countries = [] } = useCountries();
  const { states } = useStates(value.countryId);
  const selectedCountry = countries.find((country) => country.id === value.countryId);
  const selectedState = states.find((state) => state.id === value.stateId);
  const cascade = resolveHqCascade(selectedCountry, value.stateId);

  // Debounced so a city search fires per typing pause, not per keystroke.
  const debouncedCityQuery = useDebouncedValue(cityQuery);
  const { data: cityResults = [], isFetching: isCitySearching } = useCitySearch(
    cascade.cityScope,
    debouncedCityQuery,
  );

  const countryOptions = countries
    .filter((country) => country.name.toLowerCase().includes(countryQuery.toLowerCase()))
    .map((country) => ({ value: String(country.id), label: country.name }));

  const stateOptions = states
    .filter((state) => state.name.toLowerCase().includes(stateQuery.toLowerCase()))
    .map((state) => ({ value: String(state.id), label: state.name }));

  return (
    <>
      <ComboboxField
        label={countryLabel}
        labelVariant={labelVariant}
        inputValue={countryQuery}
        onInputChange={setCountryQuery}
        selectedLabel={selectedCountry?.name}
        options={countryOptions}
        error={errors?.countryId}
        onSelect={(option) => {
          // A different country invalidates the state and city under it.
          onChange({ countryId: Number(option.value) });
          setCountryQuery('');
          setStateQuery('');
          setCityQuery('');
        }}
      />
      {cascade.showState && (
        <ComboboxField
          label={stateLabel}
          labelVariant={labelVariant}
          inputValue={stateQuery}
          onInputChange={setStateQuery}
          selectedLabel={selectedState?.name}
          options={stateOptions}
          error={errors?.stateId}
          onSelect={(option) => {
            onChange({ countryId: value.countryId, stateId: Number(option.value) });
            setStateQuery('');
            setCityQuery('');
          }}
        />
      )}
      <ComboboxField
        label={cityLabel}
        labelVariant={labelVariant}
        disabled={cascade.cityScope === undefined}
        inputValue={cityQuery}
        onInputChange={setCityQuery}
        selectedLabel={value.cityName}
        loading={isCitySearching}
        options={cityResults.map((city) => ({ value: String(city.id), label: city.name }))}
        error={errors?.cityId}
        onSelect={(option) => {
          onChange({
            countryId: value.countryId,
            stateId: cascade.cityStateId,
            cityId: Number(option.value),
            cityName: option.label,
          });
          setCityQuery('');
        }}
      />
    </>
  );
}
